import React, { useEffect, useState } from "react";
import {
  Button,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  LocalShipping as LocalShippingIcon,
  AccountBalance as AccountBalanceIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import APIHelper from "../utils/APIHelper";

export default function SettingsPage({ token }) {
  const [form, setForm] = useState({
    // Sender Information
    senderName: "",
    senderEmail: "",
    senderPhone: "",
    senderCompanyName: "",
    // Origin Address
    originAddress: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      country: "India",
      countryCode: "IN",
      pinCode: "",
      type: "Residential",
    },
    // Return Address
    returnAddress: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      country: "India",
      countryCode: "IN",
      pinCode: "",
      type: "Residential",
    },
    // Default Package Info
    defaultPackageWeight: 1,
    defaultPackageDimensions: {
      length: 12,
      width: 12,
      height: 12,
    },
  });
  const [bankDetails, setBankDetails] = useState({
    accountNumber: "",
    ifsc: "",
    accountHolderName: "",
    upiId: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bankDetailsLoading, setBankDetailsLoading] = useState(false);
  const [bankDetailsSaving, setBankDetailsSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [bankDetailsMessage, setBankDetailsMessage] = useState("");
  const [bankDetailsError, setBankDetailsError] = useState("");
  const [selectedMenu, setSelectedMenu] = useState("shipping"); // Default to shipping settings

  const fetchBankDetails = async () => {
    try {
      setBankDetailsLoading(true);
      const response = await APIHelper.getBankDetails(token);
      if (response.bankDetails) {
        setBankDetails({
          accountNumber: response.bankDetails.accountNumber || "",
          ifsc: response.bankDetails.ifsc || "",
          accountHolderName: response.bankDetails.accountHolderName || "",
          upiId: response.bankDetails.upiId || "",
        });
      }
    } catch (e) {
      console.error("Error fetching bank details:", e);
    } finally {
      setBankDetailsLoading(false);
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const settings = await APIHelper.getShippingSettings(token);
        if (settings) {
          setForm({
            senderName: settings.senderName || "",
            senderEmail: settings.senderEmail || "",
            senderPhone: settings.senderPhone || "",
            senderCompanyName: settings.senderCompanyName || "",
            originAddress: settings.originAddress || {
              line1: "",
              line2: "",
              city: "",
              state: "",
              country: "India",
              countryCode: "IN",
              pinCode: "",
              type: "Residential",
            },
            returnAddress: settings.returnAddress || {
              line1: "",
              line2: "",
              city: "",
              state: "",
              country: "India",
              countryCode: "IN",
              pinCode: "",
              type: "Residential",
            },
            defaultPackageWeight: settings.defaultPackageWeight || 1,
            defaultPackageDimensions: settings.defaultPackageDimensions || {
              length: 12,
              width: 12,
              height: 12,
            },
          });
        }
      } catch (e) {
        console.error("Error fetching shipping settings:", e);
        setError("Failed to load settings");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchSettings();
      fetchBankDetails();
    }
  }, [token]);

  const handleChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setForm((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
        } else {
          setForm((prev) => ({
            ...prev,
            [field]: value,
          }));
        }
  };

  const handleNestedChange = (parent, child, value) => {
    setForm((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [child]: value,
      },
    }));
  };

  const handleSave = async () => {
    try {
      setMessage("");
      setError("");
      setSaving(true);
      await APIHelper.updateShippingSettings(token, form);
      setMessage("Shipping settings saved successfully.");
      setTimeout(() => setMessage(""), 3000);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const useSameAsOrigin = () => {
    setForm((prev) => ({
      ...prev,
      returnAddress: { ...prev.originAddress },
    }));
  };

  const handleBankDetailsChange = (field, value) => {
    setBankDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveBankDetails = async () => {
    try {
      setBankDetailsMessage("");
      setBankDetailsError("");
      setBankDetailsSaving(true);
      await APIHelper.updateBankDetails(token, bankDetails);
      setBankDetailsMessage("Bank details saved successfully.");
      setTimeout(() => setBankDetailsMessage(""), 3000);
    } catch (e) {
      setBankDetailsError(
        e?.response?.data?.error ||
          e?.response?.data?.details ||
          e.message ||
          "Failed to save bank details"
      );
    } finally {
      setBankDetailsSaving(false);
    }
  };

  const menuItems = [
    { id: "shipping", label: "Shipping Settings", icon: <LocalShippingIcon /> },
    { id: "bank", label: "Bank Details", icon: <AccountBalanceIcon /> },
  ];

  const renderShippingSettings = () => (
    <>
      <Typography variant="h6" className="text-xl font-bold text-gray-900 dark:text-white mb-2">
        Shipping Settings
      </Typography>
      <Typography variant="body2" className="text-gray-600 dark:text-gray-400 mb-6">
        Configure your sender information and warehouse address for shipping label generation.
      </Typography>

      {message && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
          <div className="flex items-center justify-between">
            <Typography className="text-sm text-green-800 dark:text-green-200">
              ✅ {message}
            </Typography>
            <button
              onClick={() => setMessage("")}
              className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
          <div className="flex items-center justify-between">
            <Typography className="text-sm text-red-800 dark:text-red-200">
              ⚠️ {error}
            </Typography>
            <button
              onClick={() => setError("")}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Sender Information */}
        <div>
          <Typography variant="subtitle1" className="text-lg font-bold text-gray-900 dark:text-white mb-4">
            Sender Information
          </Typography>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              fullWidth
              label="Company Name"
              value={form.senderCompanyName}
              onChange={(e) => handleChange("senderCompanyName", e.target.value)}
              placeholder="Your Company"
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
            <TextField
              fullWidth
              label="Sender Name"
              value={form.senderName}
              onChange={(e) => handleChange("senderName", e.target.value)}
              placeholder="John Doe"
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={form.senderEmail}
              onChange={(e) => handleChange("senderEmail", e.target.value)}
              placeholder="sender@example.com"
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
            <TextField
              fullWidth
              label="Phone"
              value={form.senderPhone}
              onChange={(e) => handleChange("senderPhone", e.target.value)}
              placeholder="+91 9876543210"
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
          </div>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 pt-4"></div>

        {/* Origin Address (Warehouse) */}
        <div>
          <Typography variant="subtitle1" className="text-lg font-bold text-gray-900 dark:text-white mb-4">
            Origin Address (Warehouse/Pickup Location)
          </Typography>
          <div className="grid grid-cols-1 gap-4">
            <TextField
              fullWidth
              label="Address Line 1"
              value={form.originAddress.line1}
              onChange={(e) => handleNestedChange("originAddress", "line1", e.target.value)}
              placeholder="Street address, building number"
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
            <TextField
              fullWidth
              label="Address Line 2"
              value={form.originAddress.line2}
              onChange={(e) => handleNestedChange("originAddress", "line2", e.target.value)}
              placeholder="Apartment, suite, unit, etc."
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextField
                fullWidth
                label="City"
                value={form.originAddress.city}
                onChange={(e) => handleNestedChange("originAddress", "city", e.target.value)}
                placeholder="Mumbai"
                className="bg-white dark:bg-gray-800"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.75rem',
                  },
                }}
              />
              <TextField
                fullWidth
                label="State"
                value={form.originAddress.state}
                onChange={(e) => handleNestedChange("originAddress", "state", e.target.value)}
                placeholder="Maharashtra"
                className="bg-white dark:bg-gray-800"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.75rem',
                  },
                }}
              />
              <TextField
                fullWidth
                label="PIN Code"
                value={form.originAddress.pinCode}
                onChange={(e) => handleNestedChange("originAddress", "pinCode", e.target.value)}
                placeholder="400001"
                className="bg-white dark:bg-gray-800"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.75rem',
                  },
                }}
              />
              <TextField
                fullWidth
                label="Country"
                value={form.originAddress.country}
                onChange={(e) => handleNestedChange("originAddress", "country", e.target.value)}
                placeholder="India"
                className="bg-white dark:bg-gray-800"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.75rem',
                  },
                }}
              />
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 pt-4"></div>

        {/* Return Address */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <Typography variant="subtitle1" className="text-lg font-bold text-gray-900 dark:text-white">
              Return Address
            </Typography>
            <Button 
              size="small" 
              onClick={useSameAsOrigin} 
              className="text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 normal-case rounded-xl"
            >
              Use Same as Origin
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-4">
            <TextField
              fullWidth
              label="Address Line 1"
              value={form.returnAddress.line1}
              onChange={(e) => handleNestedChange("returnAddress", "line1", e.target.value)}
              placeholder="Street address, building number"
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
            <TextField
              fullWidth
              label="Address Line 2"
              value={form.returnAddress.line2}
              onChange={(e) => handleNestedChange("returnAddress", "line2", e.target.value)}
              placeholder="Apartment, suite, unit, etc."
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextField
                fullWidth
                label="City"
                value={form.returnAddress.city}
                onChange={(e) => handleNestedChange("returnAddress", "city", e.target.value)}
                placeholder="Mumbai"
                className="bg-white dark:bg-gray-800"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.75rem',
                  },
                }}
              />
              <TextField
                fullWidth
                label="State"
                value={form.returnAddress.state}
                onChange={(e) => handleNestedChange("returnAddress", "state", e.target.value)}
                placeholder="Maharashtra"
                className="bg-white dark:bg-gray-800"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.75rem',
                  },
                }}
              />
              <TextField
                fullWidth
                label="PIN Code"
                value={form.returnAddress.pinCode}
                onChange={(e) => handleNestedChange("returnAddress", "pinCode", e.target.value)}
                placeholder="400001"
                className="bg-white dark:bg-gray-800"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.75rem',
                  },
                }}
              />
              <TextField
                fullWidth
                label="Country"
                value={form.returnAddress.country}
                onChange={(e) => handleNestedChange("returnAddress", "country", e.target.value)}
                placeholder="India"
                className="bg-white dark:bg-gray-800"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.75rem',
                  },
                }}
              />
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 pt-4"></div>

        {/* Default Package Information */}
        <div>
          <Typography variant="subtitle1" className="text-lg font-bold text-gray-900 dark:text-white mb-4">
            Default Package Information
          </Typography>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <TextField
              fullWidth
              label="Weight (kg)"
              type="number"
              value={form.defaultPackageWeight}
              onChange={(e) => handleChange("defaultPackageWeight", parseFloat(e.target.value) || 1)}
              inputProps={{ min: 0.1, step: 0.1 }}
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
            <TextField
              fullWidth
              label="Length (cm)"
              type="number"
              value={form.defaultPackageDimensions.length}
              onChange={(e) =>
                handleNestedChange(
                  "defaultPackageDimensions",
                  "length",
                  parseFloat(e.target.value) || 12
                )
              }
              inputProps={{ min: 1 }}
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
            <TextField
              fullWidth
              label="Width (cm)"
              type="number"
              value={form.defaultPackageDimensions.width}
              onChange={(e) =>
                handleNestedChange(
                  "defaultPackageDimensions",
                  "width",
                  parseFloat(e.target.value) || 12
                )
              }
              inputProps={{ min: 1 }}
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
            <TextField
              fullWidth
              label="Height (cm)"
              type="number"
              value={form.defaultPackageDimensions.height}
              onChange={(e) =>
                handleNestedChange(
                  "defaultPackageDimensions",
                  "height",
                  parseFloat(e.target.value) || 12
                )
              }
              inputProps={{ min: 1 }}
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
          </div>
        </div>

        <div className="pt-2">
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-8 py-3.5 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 normal-case"
          >
            {saving ? <CircularProgress size={24} className="text-white" /> : "Save Settings"}
          </Button>
        </div>
      </div>
    </>
  );

  const renderBankDetails = () => (
    <>
      <Typography variant="h6" className="text-xl font-bold text-gray-900 dark:text-white mb-2">
        Bank Details
      </Typography>
      <Typography variant="body2" className="text-gray-600 dark:text-gray-400 mb-6">
        Add your bank account details to receive payouts. Required for requesting payouts.
      </Typography>

      {bankDetailsMessage && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
          <div className="flex items-center justify-between">
            <Typography className="text-sm text-green-800 dark:text-green-200">
              ✅ {bankDetailsMessage}
            </Typography>
            <button
              onClick={() => setBankDetailsMessage("")}
              className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {bankDetailsError && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
          <div className="flex items-center justify-between">
            <Typography className="text-sm text-red-800 dark:text-red-200">
              ⚠️ {bankDetailsError}
            </Typography>
            <button
              onClick={() => setBankDetailsError("")}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {bankDetailsLoading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <CircularProgress />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              fullWidth
              label="Account Holder Name"
              value={bankDetails.accountHolderName}
              onChange={(e) => handleBankDetailsChange("accountHolderName", e.target.value)}
              placeholder="John Doe"
              required
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
            <TextField
              fullWidth
              label="Account Number"
              value={bankDetails.accountNumber}
              onChange={(e) => handleBankDetailsChange("accountNumber", e.target.value)}
              placeholder="1234567890"
              required
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
            <TextField
              fullWidth
              label="IFSC Code"
              value={bankDetails.ifsc}
              onChange={(e) => handleBankDetailsChange("ifsc", e.target.value.toUpperCase())}
              placeholder="SBIN0001234"
              required
              helperText="Format: 4 letters + 0 + 6 alphanumeric"
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
            <TextField
              fullWidth
              label="UPI ID (Optional)"
              value={bankDetails.upiId}
              onChange={(e) => handleBankDetailsChange("upiId", e.target.value)}
              placeholder="yourname@paytm"
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
          </div>

          <div className="pt-2">
            <Button
              variant="contained"
              onClick={handleSaveBankDetails}
              disabled={bankDetailsSaving}
              className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-8 py-3.5 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 normal-case"
            >
              {bankDetailsSaving ? <CircularProgress size={24} className="text-white" /> : "Save Bank Details"}
            </Button>
          </div>
        </div>
      )}
    </>
  );

  if (loading && selectedMenu === "shipping") {
    return (
      <div className="card-modern p-6">
        <div className="flex justify-center items-center min-h-[200px]">
          <CircularProgress />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <SettingsIcon className="text-primary-600 dark:text-primary-400" />
        <Typography variant="h6" className="text-2xl font-bold text-gray-900 dark:text-white">
          Settings
        </Typography>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Left Menu Pane */}
        <div className="card-modern p-2 w-full md:w-64 flex-shrink-0 h-fit sticky top-4">
          <nav>
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setSelectedMenu(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 mb-1 ${
                  selectedMenu === item.id
                    ? "bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 shadow-sm"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                <span className={`text-xl ${selectedMenu === item.id ? "text-primary-600 dark:text-primary-400" : "text-gray-500 dark:text-gray-400"}`}>
                  {item.icon}
                </span>
                <span className="font-medium text-base">
                  {item.label}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* Right Content Pane */}
        <div className="card-modern p-6 flex-1">
          {selectedMenu === "shipping" && renderShippingSettings()}
          {selectedMenu === "bank" && renderBankDetails()}
        </div>
      </div>
    </div>
  );
}
